import React, { useMemo } from "react"
import PropTypes from "prop-types"
import PhoneInput from "react-phone-number-input"
import en from "react-phone-number-input/locale/en"
import ar from "react-phone-number-input/locale/ar"
import { useTranslation } from "react-i18next"
import {
  DEFAULT_PHONE_COUNTRY,
  isAtMaxPhoneLength,
  resolvePhoneCountry,
  sanitizePhoneInput,
  trimPhoneToMaxLength,
} from "utils/phoneUtils"
import PhoneCountrySelect from "components/Shared/PhoneCountrySelect"
import "react-phone-number-input/style.css"
import "./phone-input.scss"

const isDigitKey = key => /^\d$/.test(key)

/**
 * International phone field with country flag + dial code for controlled forms.
 */
const PhoneNumberField = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  defaultCountry = DEFAULT_PHONE_COUNTRY,
  countries,
  disabled = false,
  readOnly = false,
  className = "",
  placeholder,
  hasError = false,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const labels = language === "sa" ? ar : en
  const fieldId = id || name
  const allowedCountries = useMemo(() => {
    if (!Array.isArray(countries) || !countries.length) {
      return undefined
    }
    return [
      ...new Set(
        countries
          .map(code => String(code || "").trim().toUpperCase())
          .filter(Boolean)
      ),
    ]
  }, [countries])
  const countryLocked = allowedCountries?.length === 1
  const effectiveDefaultCountry = useMemo(() => {
    const preferred = String(defaultCountry || DEFAULT_PHONE_COUNTRY).toUpperCase()
    if (!allowedCountries?.length) {
      return preferred || DEFAULT_PHONE_COUNTRY
    }
    if (allowedCountries.includes(preferred)) {
      return preferred
    }
    return allowedCountries[0]
  }, [allowedCountries, defaultCountry])
  const resolvedCountry = useMemo(() => {
    const resolved = resolvePhoneCountry(value, effectiveDefaultCountry)
    if (!allowedCountries?.length) {
      return resolved
    }
    if (allowedCountries.includes(resolved)) {
      return resolved
    }
    return effectiveDefaultCountry
  }, [value, effectiveDefaultCountry, allowedCountries])

  const applyPhoneChange = nextValue => {
    const sanitized = sanitizePhoneInput(
      nextValue || "",
      value || "",
      effectiveDefaultCountry
    )
    onChange?.(sanitized || undefined)
  }

  return (
    <div className={`formik-phone-field ${className}`.trim()}>
      <PhoneInput
        id={fieldId}
        international
        countryCallingCodeEditable={false}
        defaultCountry={effectiveDefaultCountry}
        country={resolvedCountry}
        labels={labels}
        placeholder={placeholder || t("Phone Number 13")}
        value={value || undefined}
        onChange={applyPhoneChange}
        onCountryChange={newCountry => {
          if (!value || readOnly || disabled || countryLocked || !newCountry) {
            return
          }
          const trimmed = trimPhoneToMaxLength(value, newCountry)
          if (trimmed !== value) {
            onChange?.(trimmed || undefined)
          }
        }}
        onBlur={onBlur}
        disabled={disabled}
        readOnly={readOnly}
        {...(allowedCountries
          ? {
              countries: allowedCountries,
              addInternationalOption: false,
            }
          : {})}
        countrySelectComponent={PhoneCountrySelect}
        countrySelectProps={
          readOnly || countryLocked
            ? {
                readOnly: true,
                tabIndex: -1,
                "aria-hidden": true,
                hideArrow: true,
              }
            : {
                dir: language === "sa" ? "rtl" : "ltr",
              }
        }
        className={`PhoneInput--bootstrap ${hasError ? "PhoneInput--invalid" : ""} ${
          readOnly ? "PhoneInput--readOnly" : ""
        } ${countryLocked ? "PhoneInput--countryLocked" : ""}`.trim()}
        numberInputProps={{
          id: `${fieldId}-number`,
          name,
          className: `form-control ${hasError ? "is-invalid" : ""}`,
          readOnly: readOnly || undefined,
          dir: "ltr",
          style: {
            unicodeBidi: "bidi-override",
            textAlign: language === "sa" ? "right" : "left",
          },
          onKeyDown: event => {
            if (readOnly || disabled || event.ctrlKey || event.metaKey) {
              return
            }
            if (!isDigitKey(event.key)) {
              return
            }
            if (isAtMaxPhoneLength(value, effectiveDefaultCountry)) {
              event.preventDefault()
            }
          },
          onPaste: event => {
            if (readOnly || disabled) return
            const pasted = (event.clipboardData?.getData("text") || "").trim()
            if (!pasted) return
            event.preventDefault()
            applyPhoneChange(pasted)
          },
        }}
      />
    </div>
  )
}

PhoneNumberField.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  defaultCountry: PropTypes.string,
  countries: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  hasError: PropTypes.bool,
}

export default PhoneNumberField
